import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSite } from "@/context/SiteContext";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { useTemplate } from "@/context/TemplateContext";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [logo, setLogo] = useState("https://public.readdy.ai/ai/img_res/1ebe5b90-bc3d-41ad-9f51-693742fb28f5.png");
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isLoggedIn, isAdmin, logout } = useAuth();
  const { siteId, loading: siteLoading, siteName: contextSiteName } = useSite();
  const { siteConfig, navMenus, navCategories } = useSiteConfig();
  // VIP 从公开站点配置读取（非 admin 接口，普通用户也能获取）
  const enableVip = siteConfig?.enable_vip ?? true;
  const { templateId } = useTemplate();
  const isModern = templateId === "modern";

  // 根据模板动态选择主题色（确保 Tailwind JIT 扫描到所有类名）
  const theme = {
    navHoverLightBg: isModern ? "hover:text-blue-600 hover:bg-blue-50" : "hover:text-orange-600 hover:bg-orange-50",
    accentText: isModern ? "text-blue-600" : "text-orange-600",
    accentBorder: isModern ? "border-blue-300" : "border-orange-300",
    accentBg: isModern ? "bg-blue-600" : "bg-orange-500",
    accentBgHover: isModern ? "hover:bg-blue-700" : "hover:bg-orange-600",
    vipColor: isModern ? "text-amber-500" : "text-orange-600",
    // 移动端/用户菜单 hover 组合
    menuHover: isModern ? "hover:bg-blue-50 hover:text-blue-600" : "hover:bg-orange-50 hover:text-orange-600",
    // 登录按钮组合（含边框+文字+hover背景）
    loginLightBg: isModern ? "border-blue-300 text-blue-600 hover:bg-blue-50" : "border-orange-300 text-orange-600 hover:bg-orange-50",
  };

  // 网站名称直接使用 SiteContext 中的 siteName（由 SiteConfigContext 从后台设置同步）
  const siteName = contextSiteName;

  // Only use transparent header on homepage (before scroll)
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 根据站点配置更新 Logo
  useEffect(() => {
    if (!siteConfig) return;

    if (siteConfig.logo) {
      setLogo(siteConfig.logo);
      setLogoError(false);
    }
  }, [siteConfig]);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate("/");
  };

  // Determine if we should show dark text on light background
  const showLightBg = !isHomePage || isScrolled;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showLightBg
          ? "bg-white/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            {logo && !logoError && (
              <img
                src={logo}
                alt={siteName}
                className="h-10 md:h-12 w-auto object-contain"
                onError={() => setLogoError(true)}
              />
            )}
            {logoError && logo && (
              <div className={`h-10 md:h-12 w-auto aspect-square rounded-lg bg-gradient-to-br ${isModern ? 'from-blue-400 to-blue-600' : 'from-orange-400 to-orange-600'} flex items-center justify-center text-white font-bold text-sm md:text-base`}>
                {siteName ? siteName.charAt(0).toUpperCase() : 'S'}
              </div>
            )}
            {siteName && (
              <span
                className={`text-lg md:text-xl font-bold transition-colors ${
                  showLightBg ? "text-gray-800" : "text-white"
                }`}
              >
                {siteName}
              </span>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                showLightBg
                  ? `text-gray-700 ${theme.navHoverLightBg}`
                  : "text-white/90 hover:text-white hover:bg-white/10"
              }`}
            >
              首页
            </Link>
            {navMenus.map((menu) => (
              <Link
                key={menu.id}
                to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                     menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                     menu.type === 'link' ? (menu.target_url || '#') : '#'}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  showLightBg
                    ? `text-gray-700 ${theme.navHoverLightBg}`
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                {menu.name}
              </Link>
            ))}
            {navCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  showLightBg
                    ? `text-gray-700 ${theme.navHoverLightBg}`
                    : "text-white/90 hover:text-white hover:bg-white/10"
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-3">
            {isLoggedIn ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-black/5 transition-colors"
                >
                  <img
                    src={user?.avatar || "https://readdy.ai/api/search-image?query=simple%20neutral%20generic%20user%20avatar%20portrait%20clean%20white%20background%20minimal%20professional%20headshot&width=100&height=100&seq=default-avatar-001&orientation=squarish"}
                    alt="avatar"
                    className="w-8 h-8 rounded-full object-cover bg-gray-100"
                  />
                  <span
                    className={`text-sm font-medium hidden md:block ${
                      showLightBg ? "text-gray-700" : "text-white"
                    }`}
                  >
                    {user?.nickname}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line ${
                      showLightBg ? "text-gray-500" : "text-white/70"
                    }`}
                  />
                </button>
                {isUserMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2">
                    <Link
                      to="/dashboard"
                      className={`flex items-center gap-2 px-4 py-2 text-sm text-gray-700 ${theme.menuHover}`}
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <i className="ri-user-line" />
                      会员中心
                    </Link>
                    <Link
                      to="/dashboard/my-listings"
                      className={`flex items-center gap-2 px-4 py-2 text-sm text-gray-700 ${theme.menuHover}`}
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <i className="ri-file-list-line" />
                      我的发布
                    </Link>
                    {enableVip && (
                      <Link
                        to="/vip"
                        className={`flex items-center gap-2 px-4 py-2 text-sm ${theme.vipColor} ${theme.menuHover}`}
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <i className="ri-vip-crown-line" />
                        开通VIP
                      </Link>
                    )}
                    {isAdmin && (
                      <>
                        <div className="border-t border-gray-100 my-1" />
                        <Link
                          to="/admin"
                          className={`flex items-center gap-2 px-4 py-2 text-sm text-gray-700 ${theme.menuHover}`}
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          <i className="ri-shield-star-line" />
                          后台管理
                        </Link>
                      </>
                    )}
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={handleLogout}
                      className={`flex items-center gap-2 px-4 py-2 text-sm text-gray-700 ${theme.menuHover} w-full text-left`}
                    >
                      <i className="ri-logout-box-line" />
                      退出登录
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <button
                  onClick={() => navigate("/login")}
                  className={`hidden md:block px-4 py-2 text-sm font-medium rounded-full border transition-colors whitespace-nowrap ${
                    showLightBg
                      ? theme.loginLightBg
                      : "border-white/40 text-white hover:bg-white/10"
                  }`}
                >
                  登录 / 注册
                </button>
              </>
            )}

            <button
              onClick={() => navigate("/publish")}
              className={`px-4 py-2 text-sm font-medium rounded-full ${theme.accentBg} text-white ${theme.accentBgHover} transition-colors whitespace-nowrap shadow-sm`}
            >
              <i className="ri-add-line mr-1" />
              发布信息
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-black/5 transition-colors"
            >
              <i
                className={`ri-${
                  isMobileMenuOpen ? "close" : "menu"
                }-line text-xl ${showLightBg ? "text-gray-700" : "text-white"}`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className={`block px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg ${theme.menuHover}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              首页
            </Link>
            {navMenus.map((menu) => (
              <Link
                key={menu.id}
                to={menu.type === 'category' ? `/category/${menu.target_url || ''}` :
                     menu.type === 'page' ? `/page/${menu.target_url || ''}` :
                     menu.type === 'link' ? (menu.target_url || '#') : '#'}
                className={`block px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg ${theme.menuHover}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {menu.name}
              </Link>
            ))}
            {navCategories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`block px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg ${theme.menuHover}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {cat.name}
              </Link>
            ))}
            {!isLoggedIn && (
              <Link
                to="/login"
                className={`block px-3 py-2.5 text-sm font-medium ${theme.accentText} rounded-lg ${theme.menuHover}`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                登录 / 注册
              </Link>
            )}
            {isLoggedIn && (
              <>
                <Link
                  to="/dashboard"
                  className={`block px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg ${theme.menuHover}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  会员中心
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left px-3 py-2.5 text-sm font-medium text-gray-700 rounded-lg ${theme.menuHover}`}
                >
                  退出登录
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
